import { ReactiveFormsModule } from '@angular/forms';
import { Component, Optional, SkipSelf, Signal, output } from '@angular/core';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';

/**
 * services
 */
import { TableStateService } from '../../services';

/**
 * directives
 */
import { FormInputFieldDirective } from '@forms/directives/form-input-field/form-input-field.directive';
import { ElementTemplateDirective } from '@sharedDirectives/element-template/element-template.directive';

/**
 * components
 */
import { FieldGroupComponent } from '@forms/components/field-group/field-group.component';

/**
 * Reusable search input component for table captions.
 * 
 * This component encapsulates the search input functionality that was previously
 * inline in the table component. It accesses the table state directly via host injection,
 * following the same pattern as TableCheckboxComponent.
 * 
 * It can be used in default table captions or included in custom caption templates
 * without needing to pass any inputs.
 */
@Component({
    selector: 'app-table-search',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        /**
         * libs
         */
        TranslatePipe,
        /**
         * directives
         */
        FormInputFieldDirective,
        ElementTemplateDirective,
        /**
         * components
         */
        FieldGroupComponent,
    ],
    templateUrl: './table-search.component.html',
    styleUrl: './table-search.component.scss'
})
export class TableSearchComponent {

    /**
     * Output event emitted when search value changes
     */
    public onSearchChange = output<string>();

    /**
     * Constructor
     * @param tableState Injected TableStateService from the parent TableComponent
     */
    constructor(
        @Optional()
        @SkipSelf()
        public readonly tableState: TableStateService
    ) { }

    /**
     * Loading state signal.
     * 
     * Accessed directly from TableStateService.
     */
    public get loading(): Signal<boolean> {
        return this.tableState.loading;
    }

    /**
     * Handler for search value change event
     * Emits value directly to searchInput$ subject
     * @param value The input value from the onChange event
     */
    public onSearchValueChange(value: string): void {
        this.onSearchChange.emit(value);
    }

}

